import React from 'react';
import { Container } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_WORKPACK,
  GET_ALL_WORKPACKS,
  LINK_WORKPACK_JOBS,
  REMOVE_WORKPACK,
  UPDATE_WORKPACK,
} from '../../../graph-queries/tar/workpack';
import { TWorkpack } from '../../../lib/types';
import { useSelectedAsset } from '../../../lib/util';
import { useHasTarAdminAccess, useHasUserAccess } from '../../../lib/util/auth';
import AspectsTable from '../../organisms/AspectsTable/AspectsTable';
import WorkpackForm from '../../organisms/WorkpackForm/WorkpackForm';
import { ColumnDef } from '@tanstack/react-table';
import { authorCell, useAlertText, largeTextCell } from '../../organisms/AspectsTable/util';
import { WORKPACK_STATUS_ENUM, WORKPACK_TYPE_ENUM } from '../../../lib/constants/dbConstants';

export const columns: ColumnDef<TWorkpack>[] = [
  {
    header: 'Title',
    accessorKey: 'title',
    enableSorting: false,
  },
  {
    header: 'Type',
    accessorKey: 'type',
    accessorFn: (row) => WORKPACK_TYPE_ENUM[row.type],
  },
  {
    header: 'Workpack Number',
    accessorKey: 'workpackNumber',
  },
  {
    header: 'Vendor',
    accessorKey: 'vendor',
    enableSorting: false,
  },
  {
    header: 'Vendor Job Number',
    accessorKey: 'vendorJobNumber',
  },
  {
    header: 'SOR Number',
    accessorKey: 'sorNumber',
  },
  {
    header: 'Status',
    accessorKey: 'status',
    accessorFn: (row) => WORKPACK_STATUS_ENUM[row.status],
  },
  {
    header: 'Comments',
    accessorKey: 'comments',
    enableSorting: false,
    cell: largeTextCell,
  },
  {
    header: 'Last Updated',
    accessorKey: 'updatedAt',
    cell: authorCell,
  },
];

const WorkpacksPage: React.FC = () => {
  const { alertText, setAlertText } = useAlertText();
  const hasUserAccess = useHasUserAccess();
  const hasTarAdminAccess = useHasTarAdminAccess();
  const asset = useSelectedAsset();
  const { data, loading } = useQuery(GET_ALL_WORKPACKS, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
  });
  const [createWorkpack, { loading: isCreating }] = useMutation(CREATE_WORKPACK, {
    refetchQueries: [GET_ALL_WORKPACKS],
    awaitRefetchQueries: true,
  });
  const [updateWorkpack, { loading: isUpdating }] = useMutation(UPDATE_WORKPACK, {
    refetchQueries: [GET_ALL_WORKPACKS],
    awaitRefetchQueries: true,
  });
  const [linkWorkpackJobs, { loading: isLinking }] = useMutation(LINK_WORKPACK_JOBS, {
    refetchQueries: [GET_ALL_WORKPACKS],
    awaitRefetchQueries: true,
  });
  const [removeWorkpack, { loading: isRemoving }] = useMutation(REMOVE_WORKPACK, {
    refetchQueries: [GET_ALL_WORKPACKS],
    awaitRefetchQueries: true,
  });

  const onAddSubmit = (workpack: TWorkpack) => {
    return createWorkpack({ variables: workpack }).then(() =>
      setAlertText('Workpack successfully added')
    );
  };
  const onEditSubmit = (workpack: TWorkpack) => {
    return updateWorkpack({ variables: workpack }).then(() =>
      setAlertText('Workpack successfully updated')
    );
  };

  const onLinkSubmit = (selection: TWorkpack[], jobIds: number[]) => {
    return Promise.all(
      selection.map((workpack) =>
        linkWorkpackJobs({
          variables: {
            workpackId: workpack.workpackId,
            jobIds: jobIds,
          },
        })
      )
    ).then(() =>
      setAlertText(
        `Workpack successfully linked to ${jobIds.length} job${jobIds.length > 1 ? 's' : ''}`
      )
    );
  };

  const onRemoveSubmit = (selection: TWorkpack[]) => {
    return Promise.all(
      selection.map((workpack) =>
        removeWorkpack({
          variables: { workpackId: workpack.workpackId },
        })
      )
    ).then(() => setAlertText(`Workpack successfully deleted`));
  };
  const isLoading = isCreating || isUpdating || isLinking || isRemoving || loading || !!alertText;

  return (
    <Layout>
      <Container>
        {loading ? (
          'Loading...'
        ) : (
          <AspectsTable<TWorkpack>
            hideSelectionColumn
            aspectName="workpack"
            data={(data?.allWorkpacks as unknown as TWorkpack[]) || []}
            onLinkSubmit={onLinkSubmit}
            onRemoveSubmit={onRemoveSubmit}
            columns={columns}
            alertText={alertText}
            renderAddForm={(onClose) => (
              <WorkpackForm
                onSubmit={onAddSubmit}
                onClose={onClose}
                type={'add'}
                isLoading={isLoading}
              />
            )}
            renderEditForm={(onClose, defaultValue) => (
              <WorkpackForm
                defaultValues={defaultValue}
                onSubmit={onEditSubmit}
                onClose={onClose}
                type={'edit'}
                isLoading={isLoading}
              />
            )}
            creationDisabled={!hasUserAccess}
            getRowMutationDisabled={(_row) => !hasUserAccess}
            getRowDeletionDisabled={(_row) => !hasTarAdminAccess}
            searchEnabled={true}
            searchPlaceholderText="Search the workpack tracker..."
            isLoading={isLoading}
          />
        )}
      </Container>
    </Layout>
  );
};

export default WorkpacksPage;
